/**
 * Mappers for User list and forms. No typeof/Array.isArray in components.
 */

import { getListFromApiResponse } from "../utils/apiListMappers";

export { getListFromApiResponse };

/** Team display string for table (handles teamName, team object, team string). */
export function getTeamDisplay(user) {
  if (user?.teamName) return user.teamName;
  if (user?.team?.name) return user.team.name;
  if (user?.team && typeof user.team === "string") return user.team;
  return "N/A";
}

/**
 * Designation column: prefer API `jobTitleName`, then nested/string `jobTitle`, then legacy `designation`.
 * @param {object|null|undefined} user
 * @returns {string}
 */
export function getDesignationDisplay(user) {
  if (user == null) return "N/A";
  const jtn = user.jobTitleName;
  if (jtn != null && String(jtn).trim() !== "") return String(jtn).trim();
  const jt = user.jobTitle;
  if (jt != null && typeof jt === "object" && jt.name != null && String(jt.name).trim() !== "") {
    return String(jt.name).trim();
  }
  if (typeof jt === "string" && jt.trim() !== "") return jt.trim();
  const d = user.designation;
  if (d != null && String(d).trim() !== "") return String(d).trim();
  return "N/A";
}

/** Status display for table: { text, badgeVariant } where badgeVariant is 'success' | 'danger' | null. */
export function getStatusDisplay(user) {
  const status = user?.status;
  const isActive = user?.is_active;
  if (status === "Active" || isActive === 1 || status === true)
    return { text: "Active", badgeVariant: "success" };
  if (status === "Inactive" || status === "In-Active" || isActive === 0 || status === false)
    return { text: "Inactive", badgeVariant: "danger" };
  return { text: status ?? "N/A", badgeVariant: null };
}

/**
 * One mapper for User table listing: returns display-ready values with ?? fallbacks.
 */
export function mapUserToTableRow(user) {
  const firstName = user?.firstName ?? "";
  const lastName = user?.lastName ?? "";
  const name = [firstName, lastName].filter(Boolean).join(" ") || "N/A";
  const nameForAvatar = name !== "N/A" ? name : "N";
  const emailRaw = user?.email ?? user?.userEmail ?? user?.emailAddress ?? user?.mail ?? "";
  const email = String(emailRaw ?? "").trim();
  return {
    name,
    nameForAvatar,
    designation: getDesignationDisplay(user),
    email: email || "N/A",
    mobile: user?.mobileNumber ?? user?.mobile ?? "N/A",
    role: user?.role ?? "N/A",
    teamDisplay: getTeamDisplay(user),
    statusDisplay: getStatusDisplay(user),
  };
}

/** Map list item (string or { id, name, value }) to dropdown option { label, value }. */
export function mapListItemToOption(item) {
  const label =
    typeof item === "string" ? item : (item?.name ?? item?.value ?? String(item?.id ?? item ?? ""));
  const value =
    typeof item === "string"
      ? item
      : (item?.id ?? item?.name ?? item?.value ?? String(item?.id ?? item ?? ""));
  return { label, value };
}

/** Normalize error to display string. */
export function getErrorMessage(error) {
  if (error == null) return "";
  if (typeof error === "string") return error.trim();
  return error?.message ?? String(error);
}

function isTruthyApiFlag(value) {
  return value === true || value === 1 || value === "1";
}

function isFalsyApiFlag(value) {
  return value === false || value === 0 || value === "0";
}

function isExplicitlyTrue(user, keys) {
  if (!user || typeof user !== "object") return false;
  return keys.some((key) => isTruthyApiFlag(user[key]));
}

function isExplicitlyFalse(user, keys) {
  if (!user || typeof user !== "object") return false;
  return keys.some((key) => isFalsyApiFlag(user[key]));
}

/** GET /users list: `email_verified` — 1 = verified, 0 = not verified. */
export function isUserEmailNotVerified(user) {
  if (!user || typeof user !== "object") return false;
  const raw = user.email_verified ?? user.emailVerified ?? user.isEmailVerified;
  if (raw === undefined) return false;
  if (isTruthyApiFlag(raw)) return false;
  return isFalsyApiFlag(raw) || raw === null;
}

export function getUserEmailForApi(user) {
  if (!user || typeof user !== "object") return "";
  return String(user.email ?? user.userEmail ?? user.emailAddress ?? user.mail ?? "").trim();
}

export function isUserEmailVerified(user) {
  return isExplicitlyTrue(user, ["emailVerified", "email_verified", "isEmailVerified"]);
}

export function isUserMustChangePassword(user) {
  return isExplicitlyTrue(user, ["mustChangePassword", "must_change_password"]);
}

/** Admin Users tab: show reset email only when emailVerified is true and mustChangePassword is false. */
export function canAdminSendUserPasswordReset(user) {
  if (!getUserEmailForApi(user)) return false;
  return (
    isUserEmailVerified(user) &&
    isExplicitlyFalse(user, ["mustChangePassword", "must_change_password"])
  );
}

/** Admin Users tab: show verification email when email_verified is 0 and must_change_password is 1. */
export function canAdminSendUserVerificationEmail(user) {
  if (!user?.id) return false;
  return isUserEmailNotVerified(user) && isUserMustChangePassword(user);
}
